import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  Check,
  Clock3,
  ExternalLink,
  Lightbulb,
  PlayCircle,
  Share2,
} from "lucide-react";
import type { BlogPost } from "@/content/blog";

function ShareArticleButton({ post }: { post: BlogPost }) {
  const [copied, setCopied] = useState(false);

  async function handleShare() {
    const url = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, text: post.description, url });
        return;
      }

      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }

  return (
    <button
      type="button"
      onClick={handleShare}
      className="inline-flex items-center justify-center gap-2 rounded-full bg-cta px-5 py-2.5 text-sm font-semibold text-cta-foreground shadow-sm transition hover:brightness-95"
      aria-label="Compartir este artículo"
    >
      {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
      {copied ? "Enlace copiado" : "Compartir artículo"}
    </button>
  );
}

export function BlogArticleTemplate({ post }: { post: BlogPost }) {
  const hasVideo = Boolean(post.youtubeId && post.videoTitle);
  const formattedDate = new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(post.publishedAt));

  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-lg">
        <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="FotoPasión" width={96} height={96} className="h-12 w-auto" />
            <span className="hidden text-lg font-bold sm:inline">FotoPasión</span>
          </Link>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold transition hover:border-primary/40 hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> Volver al blog
          </Link>
        </nav>
      </header>

      <main>
        <article>
          <header
            className="relative overflow-hidden"
            style={{ background: "var(--gradient-hero)" }}
          >
            <div className="absolute inset-0" style={{ background: "var(--gradient-hero-glow)" }} />
            <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 py-14 md:px-8 md:py-20 lg:grid-cols-[1.05fr_0.95fr]">
              <div>
                <nav aria-label="Ruta de navegación" className="text-sm text-muted-foreground">
                  <Link to="/" className="font-medium text-primary hover:underline">
                    Inicio
                  </Link>
                  <span className="mx-2" aria-hidden="true">
                    /
                  </span>
                  <Link to="/blog" className="font-medium text-primary hover:underline">
                    Blog
                  </Link>
                  <span className="mx-2" aria-hidden="true">
                    /
                  </span>
                  <span aria-current="page">{post.title}</span>
                </nav>
                <span className="mt-7 inline-flex rounded-full bg-white/75 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary backdrop-blur">
                  {post.category}
                </span>
                <h1 className="mt-4 text-balance font-display text-4xl font-bold leading-[1.08] tracking-tight md:text-5xl lg:text-6xl">
                  {post.title}
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                  {post.description}
                </p>
                <div className="mt-7 flex flex-wrap gap-3 text-sm font-medium text-muted-foreground">
                  <span>{formattedDate}</span>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock3 className="h-4 w-4 text-primary" /> {post.readingTime}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>{post.author}</span>
                </div>
                <div className="mt-7">
                  <ShareArticleButton post={post} />
                </div>
              </div>
              <img
                src={post.featuredImage}
                alt={post.featuredImageAlt}
                width={1200}
                height={800}
                className="aspect-[4/3] w-full rounded-[2rem] border-8 border-white/70 object-cover shadow-[var(--shadow-soft)]"
              />
            </div>
          </header>

          <div className="mx-auto grid max-w-7xl items-start gap-10 px-6 py-16 md:px-8 md:py-24 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-16">
            <aside className="rounded-3xl border border-border bg-card p-6 lg:sticky lg:top-28">
              <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                En este artículo
              </p>
              <ol className="mt-5 space-y-1">
                {hasVideo && (
                  <li>
                    <a
                      href="#video"
                      className="flex gap-3 rounded-xl px-3 py-2.5 text-sm leading-snug text-muted-foreground transition hover:bg-secondary/10 hover:text-foreground"
                    >
                      <PlayCircle className="h-4 w-4 shrink-0 text-primary" />
                      <span>Vídeo imprescindible</span>
                    </a>
                  </li>
                )}
                {post.sections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="flex gap-3 rounded-xl px-3 py-2.5 text-sm leading-snug text-muted-foreground transition hover:bg-secondary/10 hover:text-foreground"
                    >
                      <span className="font-semibold text-primary">{index + 1}</span>
                      <span>{section.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </aside>

            <div className="min-w-0">
              {hasVideo && (
                <section id="video" className="scroll-mt-28 border-b border-border pb-12 md:pb-14">
                  <div className="rounded-[2rem] border border-primary/20 bg-primary/5 p-5 md:p-8">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary">
                          <PlayCircle className="h-5 w-5" /> Vídeo imprescindible
                        </span>
                        <h2 className="mt-3 text-balance font-display text-2xl font-bold leading-snug md:text-3xl">
                          Mira, pausa y comprueba
                        </h2>
                      </div>
                      <span className="w-fit rounded-full bg-background px-3 py-1.5 text-sm font-semibold text-muted-foreground">
                        {post.videoDuration}
                      </span>
                    </div>
                    <p className="mt-4 max-w-3xl leading-7 text-muted-foreground">
                      Este artículo está pensado para trabajar junto al vídeo. Necesitas
                      visualizarlo para completar los ejemplos y las actividades que encontrarás más
                      abajo.
                    </p>
                    <div className="mt-6 overflow-hidden rounded-2xl bg-black shadow-lg">
                      <iframe
                        className="aspect-video w-full"
                        src={`https://www.youtube-nocookie.com/embed/${post.youtubeId}?rel=0`}
                        title={post.videoTitle}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </div>
                    <div className="mt-6 rounded-2xl bg-background p-5">
                      <p className="flex items-center gap-2 font-semibold text-foreground">
                        <Lightbulb className="h-5 w-5 text-coral" /> Mientras lo ves, fíjate en esto
                      </p>
                      <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
                        {(post.videoLearningGoals ?? []).map((goal) => (
                          <li key={goal} className="flex gap-3">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                            <span>{goal}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <a
                      href={`https://youtu.be/${post.youtubeId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                    >
                      Abrir el vídeo en YouTube <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </section>
              )}

              {post.sections.map((section, index) => (
                <section
                  id={section.id}
                  key={section.id}
                  className="scroll-mt-28 border-b border-border py-12 first:pt-0 md:py-14"
                >
                  <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                    Apartado {index + 1}
                  </span>
                  <h2 className="mt-2 text-balance font-display text-3xl font-bold leading-snug md:text-4xl">
                    {section.title}
                  </h2>
                  <div className="mt-6 space-y-5 text-[1.0625rem] leading-8 text-muted-foreground">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                  {section.keyPoints && (
                    <ul className="mt-7 space-y-3 rounded-2xl bg-secondary/8 p-5 text-[1.0125rem] leading-7 text-foreground/80">
                      {section.keyPoints.map((point) => (
                        <li key={point} className="flex gap-3">
                          <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.practice && (
                    <div className="mt-8 rounded-2xl border-l-4 border-coral bg-coral/10 p-5">
                      <p className="text-sm font-semibold uppercase tracking-wider text-coral">
                        Ponlo en práctica
                      </p>
                      <p className="mt-2 leading-7 text-foreground/80">{section.practice}</p>
                    </div>
                  )}
                </section>
              ))}

              <div className="mt-10 flex flex-col items-start justify-between gap-5 rounded-2xl border border-border bg-card p-6 sm:flex-row sm:items-center">
                <div>
                  <p className="font-display text-xl font-bold">¿Te ha resultado útil?</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Compártelo con otra persona que quiera seguir aprendiendo fotografía.
                  </p>
                </div>
                <ShareArticleButton post={post} />
              </div>

              <section className="mt-12 rounded-[2rem] bg-secondary p-8 text-center text-secondary-foreground shadow-[var(--shadow-soft)] md:p-12">
                <Camera className="mx-auto h-8 w-8 text-cta" />
                <h2 className="mx-auto mt-4 max-w-2xl text-balance font-display text-3xl font-bold">
                  Sigue aprendiendo con FotoPasión
                </h2>
                <p className="mx-auto mt-4 max-w-2xl leading-7 text-secondary-foreground/75">
                  Consulta la guía para principiantes o continúa con una formación práctica y
                  ordenada.
                </p>
                <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                  <Link
                    to="/guia-principiantes"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-cta px-7 py-3.5 font-semibold text-cta-foreground transition hover:brightness-95"
                  >
                    Leer la guía <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/"
                    hash="cursos"
                    className="inline-flex items-center justify-center rounded-full border border-white/30 px-7 py-3.5 font-semibold text-white transition hover:bg-white/10"
                  >
                    Ver cursos
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>

      <footer className="bg-foreground text-background">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-10 md:flex-row md:items-center md:justify-between md:px-8">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="FotoPasión" width={96} height={96} className="h-9 w-auto" />
            <span className="font-display text-lg font-bold">FotoPasión</span>
          </div>
          <span className="text-sm text-background/65">
            © {new Date().getFullYear()} FotoPasión
          </span>
        </div>
      </footer>
    </div>
  );
}
